import { useCallback, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../auth/session';
import { useProject } from '../../data/queries/use-project';
import { useProjects } from '../../data/queries/use-projects';
import {
  searchSolConversations,
  useSolConversations,
  type SolHistoryScope,
} from '../../data/queries/use-sol-conversations';
import { getSupabaseClient } from '../../data/supabase-client';
import {
  archiveSolConversation,
  deleteSolConversation,
  describeConversationWriteError,
} from '../../data/writers/sol-conversation-writers';
import type { SolConversationRow, SolConversationSearchRow } from '../../data/database.types';
import { formatInstant } from '../../lib/timezone';
import { useSolSession } from './sol-session';
import { solConversationPath, solPath } from './sol-routes';
import {
  CONVERSATION_ARCHIVED_TEXT,
  CONVERSATION_DELETE_PROMPT,
  CONVERSATION_DELETED_TEXT,
  CONVERSATION_UNARCHIVED_TEXT,
  conversationOriginLabel,
  UNTITLED_CONVERSATION,
} from './sol-wording';
import './sol.css';

/**
 * CONVERSATION HISTORY — `/sol/history` and `/projects/:projectId/sol/history`.
 * M11 / D-041.
 *
 * **THE ROUTE WRAPPER AND THE SCREEN, IN ONE FILE, AND IT READS THE ROUTE ON
 * PURPOSE**: which origin's list to show is the address's to say. Everything
 * it renders is a plain list over RLS-bound reads and two owner-scoped
 * writers; no provider is anywhere near it.
 *
 * **ORIGIN ORGANIZES THE LIST; SEARCH IS NOT SILOED** (J-7). Global history
 * lists Global-origin conversations and its search crosses every origin he
 * may read. A Project's history lists what was STARTED there and searches
 * there by default. Opening a conversation opens it where it started.
 *
 * **ARCHIVE IS REVERSIBLE AND TOUCHES NO MEMORY. DELETE IS GENUINE, BEHIND A
 * CONFIRMATION THAT SAYS WHAT IT DELETES** — and since Jerry's approved rule
 * of 2026-08-22 (migration `0024`) what it deletes is **the conversation and
 * its messages, and nothing else**. Every saved Sol memory survives, explicit
 * and automatic alike; only the memory's link back to the deleted chat is
 * detached. A memory is forgotten solely through the Memory screen or an
 * explicit "forget", and the confirmation copy says so before the tap.
 */

type Row = {
  readonly id: string;
  readonly projectId: string | null;
  readonly isGlobal: boolean;
  readonly title: string | null;
  readonly updatedAt: string;
  readonly archivedAt: string | null;
  readonly turnCount: number;
  readonly snippet: string | null;
};

function rowOfConversation(row: SolConversationRow): Row {
  return {
    id: row.id,
    projectId: row.project_id,
    isGlobal: row.started_context === 'global',
    title: row.title,
    updatedAt: row.updated_at,
    archivedAt: row.archived_at,
    turnCount: row.turn_count,
    snippet: null,
  };
}

function rowOfSearch(row: SolConversationSearchRow): Row {
  return {
    id: row.conversation_id,
    projectId: row.project_id,
    isGlobal: row.started_context === 'global',
    title: row.title,
    updatedAt: row.updated_at,
    archivedAt: row.archived_at,
    turnCount: row.turn_count,
    snippet: row.snippet,
  };
}

export function SolHistoryScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const scope: SolHistoryScope = useMemo(
    () => (projectId ? { kind: 'project', projectId } : { kind: 'global' }),
    [projectId],
  );
  const { project } = useProject(projectId);
  return (
    <SolHistory
      scope={scope}
      backTo={solPath(projectId ?? null)}
      backLabel={projectId ? 'Sol' : 'Sol'}
      heading={projectId ? `Conversations · ${project?.name ?? 'this Project'}` : 'Conversations'}
    />
  );
}

interface SolHistoryProps {
  readonly scope: SolHistoryScope;
  readonly backTo: string;
  readonly backLabel: string;
  readonly heading: string;
}

function SolHistory({ scope, backTo, backLabel, heading }: SolHistoryProps) {
  const { profile } = useSession();
  const timeZone = profile?.timezone ?? null;
  const navigate = useNavigate();
  const solSession = useSolSession();
  const [includeArchived, setIncludeArchived] = useState(false);
  const list = useSolConversations(scope, includeArchived);
  // Project names for origin labels — the same RLS-bound list the Projects
  // screen reads; never a lookup per row.
  const { projects } = useProjects();
  const projectName = useCallback(
    (id: string | null): string | null =>
      id === null ? null : (projects.find((candidate) => candidate.id === id)?.name ?? null),
    [projects],
  );

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<readonly SolConversationSearchRow[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchGeneration = useRef(0);

  const [notice, setNotice] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Leaving search mode when the box is emptied — decided in the change
  // handler, never in an effect.
  const changeQuery = (value: string): void => {
    setQuery(value);
    if (value.trim().length === 0) {
      setResults(null);
      setSearchError(null);
    }
  };

  const runSearch = (event: FormEvent): void => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length === 0) return;
    searchGeneration.current += 1;
    const ticket = searchGeneration.current;
    setSearching(true);
    setSearchError(null);
    void searchSolConversations({ query: trimmed, scope, includeArchived })
      .then((rows) => {
        if (ticket !== searchGeneration.current) return;
        setResults(rows);
      })
      .catch(() => {
        if (ticket !== searchGeneration.current) return;
        setResults([]);
        setSearchError("Couldn't search your conversations just now.");
      })
      .finally(() => {
        if (ticket === searchGeneration.current) setSearching(false);
      });
  };

  const rows: readonly Row[] =
    results !== null ? results.map(rowOfSearch) : list.conversations.map(rowOfConversation);

  const open = (row: Row): void => {
    // **OPENED WHERE IT STARTED.** The visit for that origin is pointed at
    // the conversation and its stored messages load on the Sol screen.
    solSession.openConversation(row.projectId, row.id);
    void navigate(solConversationPath(row.projectId, row.id));
  };

  const archive = (row: Row, archived: boolean): void => {
    const client = getSupabaseClient();
    if (!client || busyId !== null) return;
    setBusyId(row.id);
    setNotice(null);
    void archiveSolConversation(client, row.id, archived)
      .then(() => {
        setNotice(archived ? CONVERSATION_ARCHIVED_TEXT : CONVERSATION_UNARCHIVED_TEXT);
        setResults(null);
        setQuery('');
        return list.reload();
      })
      .catch((caught: unknown) => {
        const message = caught instanceof Error ? caught.message : String(caught);
        setNotice(describeConversationWriteError(message));
      })
      .finally(() => setBusyId(null));
  };

  const remove = (row: Row): void => {
    const client = getSupabaseClient();
    if (!client || busyId !== null) return;
    setBusyId(row.id);
    setNotice(null);
    void deleteSolConversation(client, row.id)
      .then(() => {
        // If this was the conversation a Sol screen was continuing, that visit
        // ends too: a pointer at a deleted conversation would start a fresh
        // one anyway, and the bubbles it restored would be of a deleted chat.
        if (solSession.visit(row.projectId).conversationId === row.id) {
          solSession.clear(row.projectId);
        }
        setNotice(CONVERSATION_DELETED_TEXT);
        setPendingDelete(null);
        setResults(null);
        setQuery('');
        return list.reload();
      })
      .catch((caught: unknown) => {
        const message = caught instanceof Error ? caught.message : String(caught);
        setNotice(describeConversationWriteError(message));
        setPendingDelete(null);
      })
      .finally(() => setBusyId(null));
  };

  return (
    <main className="cl-screen sol-screen">
      <p className="project-home-back">
        <Link className="cl-link" to={backTo}>
          ‹ {backLabel}
        </Link>
      </p>

      <section className="sol" aria-labelledby="sol-history-heading">
        <header className="sol-header">
          <h1 id="sol-history-heading" className="cl-screen-title">
            {heading}
          </h1>
          <p className="sol-context">
            {scope.kind === 'global'
              ? 'Conversations started in Global Sol. Search looks across every Project too.'
              : 'Conversations started from this Project.'}
          </p>
        </header>

        <form className="sol-history-search" onSubmit={runSearch} role="search">
          <label className="sol-composer-label" htmlFor="sol-history-query">
            Search conversations
          </label>
          <div className="sol-composer-actions">
            <input
              id="sol-history-query"
              className="sol-composer-input sol-history-input"
              type="search"
              value={query}
              placeholder="What did we talk about…"
              onChange={(event) => changeQuery(event.target.value)}
            />
            <button
              type="submit"
              className="cl-button sol-composer-submit"
              disabled={query.trim().length === 0 || searching}
            >
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>
        </form>

        <label className="sol-history-toggle">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />{' '}
          Show archived
        </label>

        {notice ? (
          <p className="sol-history-notice" role="status">
            {notice}
          </p>
        ) : null}
        {searchError ? (
          <p className="sol-problem" role="alert">
            {searchError}
          </p>
        ) : null}

        {list.status === 'loading' && results === null ? (
          <p className="sol-pending" role="status">
            Loading your conversations…
          </p>
        ) : null}
        {list.status === 'failed' && results === null ? (
          <p className="sol-problem" role="alert">
            {list.error}
          </p>
        ) : null}

        {list.status === 'ready' && results === null && rows.length === 0 ? (
          <p className="sol-empty">
            {includeArchived
              ? 'No conversations yet.'
              : 'No conversations yet. Ask Sol something and it will appear here.'}
          </p>
        ) : null}
        {results !== null && rows.length === 0 && !searching ? (
          <p className="sol-empty">No conversations matched that.</p>
        ) : null}

        {rows.length > 0 ? (
          <ul className="sol-history-list">
            {rows.map((row) => {
              const title = row.title?.trim() || UNTITLED_CONVERSATION;
              const when = timeZone
                ? formatInstant(row.updatedAt, timeZone, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })
                : '';
              const busy = busyId === row.id;
              return (
                <li key={row.id} className="sol-history-item">
                  <button
                    type="button"
                    className="sol-history-open"
                    onClick={() => open(row)}
                    aria-label={`Open conversation ${title}`}
                  >
                    <span className="sol-source-head">
                      <span className="sol-source-title">{title}</span>
                      {row.archivedAt !== null ? (
                        <span className="sol-source-status">ARCHIVED</span>
                      ) : null}
                    </span>
                    <span className="sol-source-meta">
                      {conversationOriginLabel(projectName(row.projectId), row.isGlobal)}
                      {when ? ` · ${when}` : ''}
                      {` · ${row.turnCount} ${row.turnCount === 1 ? 'turn' : 'turns'}`}
                    </span>
                    {row.snippet ? <span className="sol-source-caption">{row.snippet}</span> : null}
                  </button>

                  {pendingDelete === row.id ? (
                    <div className="sol-action" role="group" aria-label="Delete conversation">
                      <p className="sol-action-prompt">{CONVERSATION_DELETE_PROMPT}</p>
                      <div className="sol-action-buttons">
                        <button
                          type="button"
                          className="sol-action-confirm sol-action-confirm--danger"
                          onClick={() => remove(row)}
                          disabled={busy}
                        >
                          {busy ? 'Deleting…' : 'Delete conversation'}
                        </button>
                        <button
                          type="button"
                          className="sol-action-cancel"
                          onClick={() => setPendingDelete(null)}
                          disabled={busy}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="sol-history-actions">
                      <button
                        type="button"
                        className="sol-start-over"
                        onClick={() => archive(row, row.archivedAt === null)}
                        disabled={busy}
                      >
                        {row.archivedAt === null ? 'Archive' : 'Unarchive'}
                      </button>
                      <button
                        type="button"
                        className="sol-start-over sol-history-delete"
                        onClick={() => setPendingDelete(row.id)}
                        disabled={busy}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </main>
  );
}
